import { useState, useRef, useEffect, useMemo } from 'react';

interface Option {
  value: string;
  label: string;
}

interface Props {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  emptyLabel?: string;
  required?: boolean;
}

export default function SearchableSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Поиск...',
  emptyLabel,
  required,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = useMemo(() => {
    if (!value) return '';
    return options.find(o => o.value === value)?.label ?? '';
  }, [value, options]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    setOpen(prev => !prev);
    if (!open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
  };

  return (
    <div className="ss-root" ref={rootRef}>
      {label && <label className="form-label">{label}{required && ' *'}</label>}
      <div className="ss-control" onClick={handleToggle}>
        <span className={`ss-value ${!selectedLabel ? 'ss-placeholder' : ''}`}>
          {selectedLabel || emptyLabel || '— Не выбрано —'}
        </span>
        <span className={`ss-arrow ${open ? 'ss-arrow-open' : ''}`}>&#9662;</span>
      </div>
      {open && (
        <div className="ss-dropdown">
          <div className="ss-search-wrap">
            <input
              ref={inputRef}
              className="ss-search"
              type="text"
              placeholder={placeholder}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  setOpen(false);
                  setQuery('');
                }
              }}
            />
            {query && (
              <button className="ss-search-clear" onClick={() => setQuery('')} type="button">&times;</button>
            )}
          </div>
          <div className="ss-options">
            {emptyLabel && (
              <div
                className={`ss-option ${!value ? 'ss-option-selected' : ''}`}
                onClick={() => handleSelect('')}
              >
                {emptyLabel}
              </div>
            )}
            {filtered.length === 0 && (
              <div className="ss-no-results">Ничего не найдено</div>
            )}
            {filtered.map(o => (
              <div
                key={o.value}
                className={`ss-option ${o.value === value ? 'ss-option-selected' : ''}`}
                onClick={() => handleSelect(o.value)}
              >
                {o.label}
              </div>
            ))}
          </div>
        </div>
      )}
      {value && (
        <button className="ss-clear" onClick={handleClear} type="button" tabIndex={-1}>&times;</button>
      )}
    </div>
  );
}
